import React, { memo, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSsrSafeCallback } from '@jira/ssr__ssr-toolkit/src/hooks/useSsrSafeCallback.tsx';
import {
	getViewModeFromLocalStorage,
	setViewModeInLocalStorage,
	type ViewMode,
} from '@jira/issue__issue-preview-view-mode-storage/src/getViewModeFromLocalStorage.tsx';
import {
	viewModeActionsContext,
	type IssuePreviewViewModeActions,
} from './viewModeActionsContext.tsx';
import { viewModeStateContext } from './viewModeStateContext.tsx';

type Props = {
	children: ReactNode;
};

const getViewModeWithSidebarDefault = (): ViewMode => {
	const viewMode = getViewModeFromLocalStorage();
	return viewMode === 'DEFAULT' ? 'SIDEBAR' : viewMode;
};

const IssuePreviewViewModeProvider: React.NamedExoticComponent<Props> = memo(
	({ children }: Props) => {
		const ssrSafeCall = useSsrSafeCallback();

		const [viewMode, setViewMode] = useState<ViewMode>(() =>
			ssrSafeCall(getViewModeWithSidebarDefault, 'SIDEBAR'),
		);

		useEffect(() => {
			// eslint-disable-next-line react-you-might-not-need-an-effect/no-initialize-state
			setViewMode(getViewModeWithSidebarDefault());
		}, []);

		const actions = useMemo<IssuePreviewViewModeActions>(
			() => ({
				setDetailViewAsModal: (willSave = true) => {
					if (willSave) {
						setViewModeInLocalStorage('MODAL');
					}
					setViewMode('MODAL');
				},
				setDetailViewAsSidebar: (willSave = true) => {
					if (willSave) {
						setViewModeInLocalStorage('SIDEBAR');
					}
					setViewMode('SIDEBAR');
				},
			}),
			[],
		);

		return (
			<viewModeActionsContext.Provider value={actions}>
				<viewModeStateContext.Provider value={viewMode}>{children}</viewModeStateContext.Provider>
			</viewModeActionsContext.Provider>
		);
	},
);

IssuePreviewViewModeProvider.displayName = 'IssuePreviewViewModeProvider';

export default IssuePreviewViewModeProvider;
